import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";

import Typography from "@mui/material/Typography";

import {
  PersonalizedQuotesList,
  PersonalizedQuotesButtonsRow,
  usePersonalizedPlanData,
} from "@covertree/new-business-forms";
import { Offer } from "@covertree/new-business-graphql";
import { PersonalizedQuotesComparePlansModal } from "@covertree/new-business-modals";
import {
  chooseQuoteAndLoadExtraCoveragePrices,
  hideLoadingFrame,
  selectIsBookConversion,
  selectPolicy,
  selectShouldPlanBeSelected,
  showLoadingFrame,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

import { NewPolicyRoutes, navigateToPolicyStep } from "@routing/paths";
import { useAppDispatch } from "@redux/hooks";
import {
  PersonalizedQuotesInsuranceScoreNoHitModal,
  PersonalizedQuotesWhatsCoveredModal,
} from "./modals";

export function PersonalizedQuotesStep() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const policy = useNewBusinessSelector(selectPolicy);
  const shouldPlanBeSelected = useNewBusinessSelector(
    selectShouldPlanBeSelected
  );
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);
  const {
    coveragesFlowData,
    personalizedPlans,
    shouldDisplayPremisesLiability,
  } = usePersonalizedPlanData(policy, isBookConversion);

  const [comparePlansModalOpened, setComparePlansModalOpened] =
    useState<boolean>(false);
  const [whatsCoveredOpened, setWhatsCoveredModalOpened] =
    useState<boolean>(false);

  const unitCoverageDetails = policy.units[0].coverageDetails;

  const onPlanSelect = useCallback(
    async ({ quoteLocator }: Offer) => {
      dispatch(
        showLoadingFrame({
          maxLoadTime: 10,
          messages: [
            "Selecting preferred personalized plan",
            "Checking prices for available coverages",
            "Finding more discounts",
            "Generating custom add-ons",
          ],
        })
      );

      try {
        await dispatch(
          chooseQuoteAndLoadExtraCoveragePrices({
            quoteLocator,
          })
        ).unwrap();
        navigateToPolicyStep(
          navigate,
          NewPolicyRoutes.EXTRA_COVERAGES,
          NewPolicyRoutes.PERSONAL_DETAILS,
          dispatch
        );
      } catch {
        navigateToPolicyStep(
          navigate,
          NewPolicyRoutes.ERROR,
          NewPolicyRoutes.PERSONAL_DETAILS,
          dispatch
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(hideLoadingFrame());
  }, [dispatch]);

  return (
    <>
      <Typography variant="h4">Select a Plan</Typography>
      <PersonalizedQuotesList
        policyLocator={policy.policyLocator}
        isTenantFlow={coveragesFlowData.isTenantFlow}
        selectedQuote={shouldPlanBeSelected ? policy.selectedQuoteLocator : ""}
        shouldDisplayPremisesLiability={shouldDisplayPremisesLiability}
        personalizedPlans={personalizedPlans}
        onPlanSelect={onPlanSelect}
      />
      <PersonalizedQuotesButtonsRow
        documentUrl={policy?.units[0]?.valuationReportUrl}
        openComparePlansModal={setComparePlansModalOpened}
        openWhatsCoveredModal={setWhatsCoveredModalOpened}
      />

      <PersonalizedQuotesComparePlansModal
        personalizedPlans={personalizedPlans}
        open={comparePlansModalOpened}
        coveragesFlowData={coveragesFlowData}
        onClose={() => setComparePlansModalOpened(false)}
        onPlanSelect={onPlanSelect}
      />
      <PersonalizedQuotesWhatsCoveredModal
        open={whatsCoveredOpened}
        covered={unitCoverageDetails.covered}
        notCovered={unitCoverageDetails.notCovered}
        onClose={() => setWhatsCoveredModalOpened(false)}
      />
      <PersonalizedQuotesInsuranceScoreNoHitModal />
    </>
  );
}
